import React from 'react';

import {useGoToDeclaration} from '../../../../../hooks/use-go-to-declaration';
import {isReadOnlyOntology} from '../../../../../utils/ontology';

import {ExploreViewEditor} from './explore-view-editor';
import {useEditorModel} from '../model';
import {LocalBackdrop} from '../../../../../components/local-backdrop';
import {NotFoundView} from '../editor-backdrop/not-found-view';
import {useDocRevisionsContext} from '../../../../../providers/version';
import {BusyIndicator} from '../../../../../components/busy-indicator';
import {FindUsagesEditorEvent} from '../../../../../../webeditor/biokb-editor-adapters';
import {GlobalVersionData} from '../../../../../hooks/global-version/use-global-version-query';
import {CriticalErrorView} from '../../../../../components/critical-error/critical-error-view';
import {useDocumentSidebarState} from '../../../common/right-sidebar/hooks/use-document-sidebar-state';
import {OntologyView} from '../../../common/ontology-view/ontology-view';
import {DocumentEditorHeader} from '../document-editor-header';
import {useDocLayoutSettings} from '../../hooks/use-doc-layout-settings';
import {OntologyElement} from '../../../common/ontology-view/requests/use-ontology-element-query';
import {useDocumentQuery} from '../../hooks/requests/use-document-query';

interface Props {
    documentId: string;
    revisionId?: string;
    handleFindUsages: (event: FindUsagesEditorEvent) => void;

    documentQuery: ReturnType<typeof useDocumentQuery>;
    editorState: ReturnType<typeof useEditorModel>;
    selectedGlobalVersion: GlobalVersionData;
    onFactsSelectionChanged: (factIds: string[]) => void;
    selectedFacts: string[];
    rightSidebarProps: ReturnType<typeof useDocumentSidebarState>;
    docLayoutSettings: ReturnType<typeof useDocLayoutSettings>;
}

export const Editor = ({
    documentId,
    revisionId,
    editorState,
    handleFindUsages,
    selectedGlobalVersion,
    onFactsSelectionChanged,
    selectedFacts,
    rightSidebarProps,
    docLayoutSettings,
    documentQuery
}: Props) => {
    const {revisions, selectedRevision} = useDocRevisionsContext();

    const {goToDeclaration, focusedOntologyNode} = useGoToDeclaration(
        documentId,
        revisionId,
        rightSidebarProps
    );

    if (documentQuery.isLoading || editorState.isLoadingLastRevision) {
        return <BusyIndicator />;
    }

    if (!documentQuery.data) {
        const revisionNotFound = Boolean(revisionId) && !!revisions && !selectedRevision;

        if (documentQuery.error === 'not-found' || revisionNotFound) {
            return (
                <LocalBackdrop open variant="zerostate">
                    <NotFoundView
                        variant={documentQuery.error ? 'document' : 'revision'}
                        documentId={documentId}
                    />
                </LocalBackdrop>
            );
        }

        return (
            <LocalBackdrop open variant="zerostate">
                <CriticalErrorView />
            </LocalBackdrop>
        );
    }

    if (isReadOnlyOntology(documentQuery.data) && documentQuery.data.id) {
        return (
            <OntologyView
                mode="explore"
                document={documentQuery.data}
                versionId={selectedGlobalVersion.id}
                nodeId={focusedOntologyNode}
                onNavigate={goToDeclaration}
                onFindUsages={handleFindUsages}
            >
                {(ontologyElementInfo?: OntologyElement) => (
                    <DocumentEditorHeader
                        docInfo={documentQuery.data}
                        revisionId={revisionId}
                        editorState={editorState}
                        isBodyCollapsed={docLayoutSettings.isDocBodyCollapsed}
                        toggleIsBodyCollapsed={docLayoutSettings.toggleIsDocBodyCollapsed}
                        isGraphPanelOpen={!!selectedFacts.length}
                        ontologyElementInfo={ontologyElementInfo}
                    />
                )}
            </OntologyView>
        );
    }

    return (
        <React.Fragment>
            <DocumentEditorHeader
                docInfo={documentQuery.data}
                revisionId={revisionId}
                editorState={editorState}
                isBodyCollapsed={docLayoutSettings.isDocBodyCollapsed}
                toggleIsBodyCollapsed={docLayoutSettings.toggleIsDocBodyCollapsed}
                isGraphPanelOpen={!!selectedFacts.length}
            />

            <ExploreViewEditor
                accession={documentId}
                focusedNodeId={focusedOntologyNode}
                revisionId={revisionId}
                onFindUsages={handleFindUsages}
                isLoadingLastRevision={editorState.isLoadingLastRevision}
                revisionInfo={editorState.revisionInfo}
                documentInfo={documentQuery.data}
                selectedGlobalVersion={selectedGlobalVersion}
                onFactsSelectionChanged={onFactsSelectionChanged}
                selectedFacts={selectedFacts}
                goToDeclaration={goToDeclaration}
            />
        </React.Fragment>
    );
};
